/**
 * Canonical taxonomy + rendering helpers for engagement/client notes.
 *
 * Any note whose `note_type` is in {@link MARKDOWN_NOTE_TYPES} was authored by
 * YadidAi and is stored as markdown (headings, bullets, bold). Every surface
 * that displays a note body MUST consult this set before deciding how to
 * render it — pipe through <MarkdownDocument> when true, keep the plain
 * whitespace-pre-wrap treatment otherwise — so the app never leaks raw
 * markdown source to partners.
 *
 * When a new AI-authored note type is added (e.g. "weekly_brief",
 * "closeout_memo"), append it here and every consumer picks it up.
 */
export const MARKDOWN_NOTE_TYPES = new Set([
  "diagnostic_memo",
  "strategic_memo",
  "insight",
  // Weekly partner brief drafted by YadidAi — persisted only on approval,
  // rendered as a typeset document just like the diagnostic memo.
  "weekly_brief",
  // Advisory relationship storyline — always includes a "provisional AI
  // interpretation" banner in the surface that renders it, but the body
  // itself is markdown so the same MarkdownDocument pipeline applies.
  "health_storyline",
]);

/**
 * Human-facing labels per `note_type`. Iter 75 · Wired everywhere the app
 * used to show the raw type (or the collective word "Note") so partners
 * see the actual object kind — Meeting, Weekly Brief, Client Storyline —
 * rather than a bland "note" that undersells what the record really is.
 *
 * Backend Literal is the source of truth for the key set (see models.py
 * Note.note_type). Anything not in this map falls through to
 * `noteLabel()`'s title-cased fallback.
 */
export const NOTE_TYPE_LABELS = {
  meeting: "Meeting",
  call_log: "Call",
  insight: "Memo",
  strategic_memo: "Strategic Memo",
  diagnostic_memo: "Diagnostic Memo",
  weekly_brief: "Weekly Brief",
  health_storyline: "Partner Org Storyline",
  client_briefing: "Partner Org Briefing",
  engagement_briefing: "Engagement Briefing",
};

export const noteLabel = (noteType) => {
  if (!noteType) return "Note";
  if (NOTE_TYPE_LABELS[noteType]) return NOTE_TYPE_LABELS[noteType];
  // Fallback: title-case whatever custom type sneaks in (e.g. "field_note").
  return String(noteType).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export const isMarkdownNote = (note) =>
  !!note && MARKDOWN_NOTE_TYPES.has(note.note_type);

/**
 * Flatten markdown source into plain prose for tight 1-2 line card previews.
 * Strips headings, bullet/numbered list markers, bold/italic/inline code, and
 * fenced code blocks; collapses paragraph breaks into ` · ` separators so the
 * outline still reads. Safe on plain text (no-op).
 */
export function previewText(body) {
  if (!body) return "";
  return body
    .replace(/```[\s\S]*?```/g, " ")           // fenced code blocks
    .replace(/^#{1,6}\s+/gm, "")               // heading hashes
    .replace(/^[-*+]\s+/gm, "")                // bullet markers
    .replace(/^\s*\d+\.\s+/gm, "")             // numbered markers
    .replace(/\*\*(.+?)\*\*/g, "$1")           // bold
    .replace(/(^|[^*])\*([^*]+)\*/g, "$1$2")   // italics
    .replace(/`([^`]+)`/g, "$1")               // inline code
    .replace(/\n{2,}/g, " · ")                 // paragraph breaks → separator
    .replace(/\s+/g, " ")
    .trim();
}
